import { IEmissionFactors, IConfiguration } from './../modules/emission-factors/emission/types'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
interface IState {
  factor: IEmissionFactors
  loading: boolean
  error: string
  configuration: IConfiguration
  configurationLoading: boolean
}

const initialState: IState = {
  configuration: {
    productTaxonomies: {
      classes: [],
      categories: [],
      subcategories: [],
      functionalUnits: [{ id: 0, subcategoryId: 0, code: '', name: '' }],
    },
    eutrophicationProxyProduceTaxonomies: {
      classes: [{ id: 0, code: '', name: '' }],
      categories: [],
      subcategories: [],
      functionalUnits: [],
    },
    dataOrigins: [],
    flagDisaggregationTypes: [],
    farmDataCalculators: [],
    functionalUnits: [],
    plantProtectionTypes: [],
    plantProtectionTypeGroups: [],
    pnNExcelCalculators: [],
    productionSystems: [],
    postHarvestLossesIds: [],
    productTypeEFs: [],
    regions: [],
    countries: [],
    secondaryDataReferenceSourceType: [],
    missingImpactFactors: [],
    specialTaxonomies: {
      classes: [],
      categories: [],
      functionalUnits: [{ id: 0, categoryId: 0, code: '', name: '', state: 0 }],
    },
  },
  factor: {
    code: null,
    userEmail: '',
    lastUpdatedName: '',
    lastPublishedDate: '',
    researcherName: '',
    researcherEmail: '',
    updated: '',
    state: 0,
    id: 0,
    productType: -1,
    productId: -1,
    productNameAtSource: '',
    countryId: null,
    regionIdentifierId: -1,
    regionStateName: '',
    productionSystem: null,
    plantProtectionTypeGroup: null,
    plantProtectionType: null,
    functionalUnit: -1,
    dataOrigin: -1,
    coreVersionRelease: null,
    externalSource: null,
    farmDataCalculator: null,
    hestiaProduceId: null,
    hestiaSubmissionId: null,
    calculatorVersion: null,
    pnNExcelCalculator: null,
    mondraMAToFillMissingEutrophication: false,
    clickUpId: '',
    landUse: 0,
    biodiversity: 0,
    biodiversityPDFYear: 0,
    waterUsageIncludingWasterScarcity: 0,
    postHarvestLossesId: null,
    postHarvestLosses: null,
    postHarvestLossesNotes: [],
    comment: null,
    missingImpactFactor: 0,
    proxyCountryId: null,
    proxyProduceId: null,
    numberOfStudies: -1,
    numberOfSCycles: null,
    referenceStudy: '',
    referenceTechnologyProxyContribution: null,
    referenceSourcingCountryProxyContribution: null,
    referenceTechnologyType: '',
    referenceRegionIdentifier: -1,
    carbon: 0,
    waterUsage: 0,
    eutrophication: 0,
    secondaryDataReferenceSourceType: -1,
    dataQualityReliabilityScore: null,
    dataQualityCompletenessScore: null,
    referencesSamplingYear: null,
    referencesSamplingStartYear: null,
    referenceDataPublicationYear: null,
    dataQualityComment: '',
    referenceSourcingCountry: null,
    companyId: null,
    isVerified: false,
    verificationComments: null,
    verifiedOn: null,
    verifiedByEmail: null,
    verifiedByName: null,
    flag_L1_LandManagementCarbon: null,
    flag_L1_LandManagementNonCarbon: null,
    flag_L1_LandUseChangeCarbon: null,
    flag_L1_NonLandCarbon: null,
    flag_L1_UnverifiedRemovalsCarbon: null,
    flag_L1_VerifiedRemovalsCarbon: null,
    flag_DisaggregationType: null,
    impacts: {
      abioticDepletion: null,
      abioticDepletionFossilFuels: null,
      abioticResourceDepletionFossilFuels: null,
      acidification: null,
      aboveGroundCropResidueBurnt: null,
      aboveGroundCropResidueLeftOnField: null,
      aboveGroundCropResidueRemoved: null,
      biogenicCarbonDioxide: null,
      biogenicMethane: null,
      biogenicProductEmissions: null,
      belowGroundCropResidue: null,
      capturedBiogenicCO2WithGeologicStorage: null,
      carbonDioxide: null,
      completenessCropResidue: null,
      completenessElectricityFuel: null,
      completenessFertiliser: null,
      completenessOtherChemical: null,
      completenessPesticideVetinaryDrug: null,
      completenessProduct: null,
      completenessSeed: null,
      completenessWater: null,
      cumulativeEnergyDemand: null,
      cumulativeEnergyDemand_NonRenewable: null,
      cumulativeEnergyDemand_Renewable: null,
      damageToTerrestrialEcosystems_LandOccupation: null,
      damageToTerrestrialEcosystems_LandTransformation: null,
      deforestation: null,
      diesel: null,
      economicValueShare: null,
      electricity: null,
      ecotoxicityFreshwater: null,
      fluorinatedGHGs: null,
      fossilCarbonDioxide: null,
      fossilMethane: null,
      fugitiveEmissions: null,
      eutrophicationTerrestrial: null,
      freshwaterEcotoxicity: null,
      freshwaterEutrophication: null,
      ghgTotal_EF_PEF_EmissionsMinusRemovals: null,
      hfCs: null,
      humanCarcinogenicToxicity: null,
      humanNonCarcinogenicToxicity: null,
      humanToxicityCancer: null,
      humanToxicityNonCancer: null,
      inorganicFertiliser_K2O: null,
      inorganicFertiliser_N: null,
      inorganicFertiliser_P2O5: null,
      inputsProduction: null,
      irrigation: null,
      ionisingRadiation: null,
      kynetecProduceID: null,
      landCover: null,
      landEmissions: null,
      landManagementCO2Removals: null,
      landManagementNetBiogenicCO2Emissions: null,
      landManagementProductionEmissions: null,
      landTransformation: null,
      lime: null,
      marineEcotoxicity: null,
      marineEutrophication: null,
      methane: null,
      mineralsMetalsUse: null,
      mobileCombustion: null,
      montrealProtocolSubstance_IndustrialProcesses: null,
      montrealProtocolSubstance_InputsProduction: null,
      nF3: null,
      nitrousOxide: null,
      organicFertiliser_K2O: null,
      organicFertiliser_N: null,
      organicFertiliser_P2O5: null,
      otherFluorinatedGHG_IndustrialProcesses: null,
      otherFluorinatedGHG_InputsProduction: null,
      outsideLSRS: null,
      pfCs: null,
      particulateMatter: null,
      pesticide: null,
      photochemicalOzoneFormation: null,
      processEmissions: null,
      scope1: null,
      scope1_CH4: null,
      scope1_CO2: null,
      scope1_HFCs: null,
      scope1_N2O: null,
      scope1_NF3: null,
      scope1_PFCs: null,
      scope1_SF6: null,
      scope2: null,
      scope2_CH4: null,
      scope2_CO2: null,
      scope2_HFCs: null,
      scope2_N2O: null,
      scope2_NF3: null,
      scope2_PFCs: null,
      scope2_SF6: null,
      scope3: null,
      scope3_CH4: null,
      scope3_CO2: null,
      scope3_HFCs: null,
      scope3_N2O: null,
      scope3_NF3: null,
      scope3_PFCs: null,
      scope3_SF6: null,
      scope3Cat1_PurchasedGoodsServices: null,
      scope3Cat3_FuelEnergyRelatedActivities: null,
      sF6: null,
      seed: null,
      siteType: null,
      soilQualityIndex_TotalLandUseEffects: null,
      stationaryCombustion: null,
      stratosphericOzoneDepletion: null,
      terrestrialEcotoxicity: null,
      technologicalCO2RemovalsWithGeologicStorage: null,
      yield: null,
    },
  },
  loading: true,
  configurationLoading: true,
  error: '',
}

const factorSlice = createSlice({
  name: 'factor',
  initialState,
  reducers: {
    factorRequest(state) {
      state.loading = true
    },
    factorSuccess(state, action: PayloadAction<IEmissionFactors>) {
      state.loading = false
      state.factor = action.payload
    },
    factorFailure(state, action: PayloadAction<string>) {
      state.loading = false
      state.error = action.payload
    },
    factorConfigurationRequest(state) {
      state.configurationLoading = true
    },
    factorConfigurationFailure(state) {
      state.configurationLoading = false
    },
    configuration(state, action: PayloadAction<IConfiguration>) {
      state.configurationLoading = false
      state.configuration = action.payload
    },
    handlePublishStatus(state, action: PayloadAction<number>) {
      state.factor.state = action.payload
    },
    addNote(state, action) {
      if (state.factor[action.payload.key] == null) {
        state.factor[action.payload.key] = []
      }
      state.factor[action.payload.key].push(action.payload.value)
    },
    setDefaultFactor: () => initialState,
  },
})

export default factorSlice.reducer
export const {
  factorRequest,
  factorSuccess,
  factorFailure,
  handlePublishStatus,
  setDefaultFactor,
  configuration,
  addNote,
  factorConfigurationRequest,
  factorConfigurationFailure,
} = factorSlice.actions
